import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useCallback } from 'react';
import { roleToDefaultRoute as resolveDashboardRoute, pickPrimaryRole, Role } from '@/lib/roles';

interface PortalRoute {
  path: string;
  requiredRoles?: string[];
  fallbackPath?: string;
}

const PORTAL_ROUTES: Record<string, PortalRoute> = {
  'admin': {
    path: '/admin-portal',
    requiredRoles: ['admin', 'staff'],
    fallbackPath: '/dashboard'
  },
  'business-partners': {
    path: '/partners',
    // Admin et staff ont accès à tous les portails pour gestion
    requiredRoles: ['admin', 'staff', 'partner', 'broker', 'lender'],
    fallbackPath: '/dashboard'
  },
  'staff': {
    path: '/staff',
    requiredRoles: ['admin', 'staff'],
    fallbackPath: '/dashboard'
  },
  'dashboard': {
    path: '/dashboard',
    requiredRoles: undefined, // Tous les utilisateurs connectés
    fallbackPath: '/login'
  }
};

const INTENDED_DESTINATION_KEY = 'intended_destination';

export const useAuthNavigation = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  /**
   * Stocke la destination souhaitée dans sessionStorage
   */
  const storeIntendedDestination = useCallback((path: string) => {
    sessionStorage.setItem(INTENDED_DESTINATION_KEY, path);
  }, []);

  /**
   * Récupère et supprime la destination souhaitée
   */
  const getAndClearIntendedDestination = useCallback((): string | null => {
    const destination = sessionStorage.getItem(INTENDED_DESTINATION_KEY);
    if (destination) {
      sessionStorage.removeItem(INTENDED_DESTINATION_KEY);
    }
    return destination;
  }, []);

  /**
   * Vérifie si l'utilisateur a un des rôles requis
   */
  const hasRequiredRole = useCallback((requiredRoles?: string[]): boolean => {
    if (!requiredRoles || requiredRoles.length === 0) {
      return true; // Pas de rôle requis = accessible à tous
    }
    return user?.roles?.some(role => requiredRoles.includes(role)) || false;
  }, [user]);

  /**
   * Détermine la meilleure route de dashboard selon le rôle de l'utilisateur
   * Utilisé uniquement pour la redirection post-login (bouton Sign In)
   * Les menus Portal utilisent directement leur portal.path
   */
  const getDashboardRouteByRole = useCallback((): string => {
    // Source unique : ROLE_DASHBOARD_MAP + ROLE_PRIORITY dans src/lib/roles.ts
    const roles = (user?.roles as Role[] | undefined) ?? [];
    return resolveDashboardRoute(pickPrimaryRole(roles) as Role);
  }, [user]);

  /**
   * Navigate vers un portail avec vérification d'authentification et de rôle
   */
  const navigateToPortal = useCallback((portalKey: string) => {
    const portal = PORTAL_ROUTES[portalKey];

    console.log(`📍 navigateToPortal called for ${portalKey}`, {
      loading,
      user: user ? 'exists' : 'null',
      userEmail: user?.email,
      userRoles: user?.roles
    });

    if (!portal) {
      console.error(`Portal "${portalKey}" not found`);
      return;
    }

    // Si l'authentification est en cours de chargement, attendre
    if (loading) {
      console.log(`⏳ Authentication loading, please wait...`);
      return;
    }

    // Si pas connecté, stocker la destination et rediriger vers login
    if (!user) {
      console.log(`🔒 User not authenticated, redirecting to /login`);
      storeIntendedDestination(portal.path);
      navigate('/login', { state: { from: portal.path } });
      return;
    }

    // Si connecté, vérifier les permissions
    console.log(`🔍 Checking access to ${portalKey}:`, {
      userRoles: user?.roles,
      requiredRoles: portal.requiredRoles,
      hasAccess: hasRequiredRole(portal.requiredRoles)
    });

    if (hasRequiredRole(portal.requiredRoles)) {
      console.log(`✅ Access granted to ${portal.path}`);
      navigate(portal.path);
    } else {
      // Rediriger vers le dashboard approprié selon le rôle de l'utilisateur
      const dashboardRoute = getDashboardRouteByRole();
      console.log(`⚠️ User doesn't have required roles for ${portalKey}, redirecting to ${dashboardRoute}`);
      navigate(dashboardRoute);
    }
  }, [loading, user, navigate, storeIntendedDestination, hasRequiredRole, getDashboardRouteByRole]);

  /**
   * Après login, rediriger vers la destination voulue ou le dashboard approprié
   */
  const handlePostLoginRedirect = useCallback(() => {
    const intendedDestination = getAndClearIntendedDestination();

    if (intendedDestination) {
      // Vérifier si l'utilisateur a les permissions pour cette destination
      const portal = Object.values(PORTAL_ROUTES).find(p => p.path === intendedDestination);

      if (portal && hasRequiredRole(portal.requiredRoles)) {
        navigate(intendedDestination);
        return;
      }
    }

    // Par défaut, rediriger vers le dashboard approprié selon le rôle
    navigate(getDashboardRouteByRole());
  }, [navigate, getAndClearIntendedDestination, hasRequiredRole, getDashboardRouteByRole]);

  return {
    navigateToPortal,
    handlePostLoginRedirect,
    storeIntendedDestination,
    getDashboardRouteByRole,
    hasRequiredRole,
    loading
  };
};
